// src/Pages/SessionReportDetail/Hooks/use-session-report-detail.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import sessionsApi from 'Api/axios/sessions-api';
import useTranslation from 'Hooks/useTranslation';

const DEFAULT_FILTERS = { search: '', status: 'all' };
const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Orquesta los 3 fetches del detalle de sesión (read-only para el reporte).
 *
 * @param {string} sessionId
 */
export default function useSessionReportDetail(sessionId) {
  const { t2 } = useTranslation('SessionsReport');
  // `t2` is rebuilt on every render by useTranslation. Stash it in a ref so
  // effects can read the latest value without subscribing to it.
  const t2Ref = useRef(t2);
  useEffect(() => {
    t2Ref.current = t2;
  });

  const [session, setSession] = useState(null);
  const [stats, setStats] = useState(null);
  const [attendances, setAttendances] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE, totalItems: 0, totalPages: 0 });
  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Reset everything when the session changes
  useEffect(() => {
    setFiltersState(DEFAULT_FILTERS);
    setPageState(1);
    setPageSizeState(DEFAULT_PAGE_SIZE);
    setNotFound(false);
    setError(null);
  }, [sessionId]);

  // Fetch session + stats once per sessionId
  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [sessionRes, statsRes] = await Promise.all([
        sessionsApi.getById(sessionId),
        sessionsApi.getSessionStats(sessionId),
      ]);
      if (cancelled) return;
      if (sessionRes.error) {
        if (sessionRes.status === 404) {
          setNotFound(true);
        } else {
          setError(sessionRes.error);
          toast.error(t2Ref.current('detail.errorLoad'));
        }
        setLoading(false);
        return;
      }
      setSession(sessionRes.data || null);
      setStats(statsRes.data || null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Subscribe only to the primitive search string (so a new object reference
  // for `filters` doesn't itself retrigger fetches).
  const searchTerm = filters.search;
  const statusFilter = filters.status;

  // Fetch attendances. Debounce ONLY when `searchTerm` changes; other deps
  // (page, pageSize, status, sessionId) fetch immediately.
  const debounceRef = useRef(null);
  const lastSearchRef = useRef(searchTerm);
  useEffect(() => {
    if (!sessionId || notFound) return undefined;
    let cancelled = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const searchChanged = lastSearchRef.current !== searchTerm;
    lastSearchRef.current = searchTerm;
    const delay = searchChanged ? SEARCH_DEBOUNCE_MS : 0;

    debounceRef.current = setTimeout(async () => {
      if (cancelled) return;
      const apiFilters = {
        page,
        limit: pageSize,
        search: searchTerm?.trim() || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
      };
      const res = await sessionsApi.listAttendances(sessionId, apiFilters);
      if (cancelled) return;
      if (res.error) {
        toast.error(t2Ref.current('detail.errorLoad'));
        return;
      }
      setAttendances(res.data || []);
      if (res.pagination) {
        setPagination({
          page: res.pagination.page,
          pageSize: res.pagination.pageSize,
          totalItems: res.pagination.totalItems,
          totalPages: res.pagination.totalPages,
        });
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
  }, [sessionId, searchTerm, statusFilter, page, pageSize, notFound]);

  const setFilters = useCallback(next => {
    setFiltersState(next);
    setPageState(1);
  }, []);

  const setPage = useCallback(nextPage => setPageState(nextPage), []);

  const setPageSize = useCallback(nextSize => {
    setPageSizeState(nextSize);
    setPageState(1);
  }, []);

  return {
    session,
    stats,
    attendances,
    pagination,
    filters,
    setFilters,
    setPage,
    setPageSize,
    loading,
    error,
    notFound,
  };
}
